import { useCopyToClipboard } from '@uidotdev/usehooks'
import {
  Building2,
  ContactRound,
  Copy,
  ExternalLink,
  Mail,
  MapPin,
  Pencil,
  Phone,
  User,
} from 'lucide-react'
import { toast } from 'sonner'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { Can } from '@/lib/casl'
import { formatCustomerAddressLines as formatAddressLines } from '@/lib/services/customers/customers.address'
import { cn } from '@/lib/utils'
import { IconActionButton } from '@/components/patterns/admin-page'
import { Badge } from '@/components/ui/badge'
import { CustomerTypeBadge } from './CustomerTypeBadge'
import { buildDeliveryAddressMapsUrl as buildAddressMapsUrl } from '@/components/orders/sales-order-detail.helpers'
import {
  DetailCard,
  DetailIconBadge,
  DetailWell,
  detailPaleIconClass,
  detailSurfaceActionClass,
} from '@/components/patterns/detail-page'
import { Typography } from '@/components/ui/typography'

const contactDetailValueClass = 'block min-w-0 break-words text-base/6 font-medium'

function AddressBlock({ title, address, action = null, children = null, hideAddress = false }) {
  const lines = formatAddressLines(address, {
    includeCountry: true,
    combineStreetLines: true,
  })
  const mapsUrl = buildAddressMapsUrl(address)
  const addressContent = hideAddress ? null : lines.length > 0 ? (
    <div className="mt-2 text-base/6">
      {lines.map((line, index) => (
        <p key={index}>{line}</p>
      ))}
    </div>
  ) : (
    <Typography variant="supporting" className="mt-1 block">
      No address added yet.
    </Typography>
  )

  return (
    <DetailWell tone="mint" className="relative">
      <div className="flex items-start gap-3">
        <MapPin className={cn(detailPaleIconClass, 'mt-0.5')} />
        <div className="min-w-0 flex-1 pr-10">
          <Typography variant="eyebrow" className="block">
            {title}
          </Typography>
          {!hideAddress && mapsUrl && lines.length > 0 ? (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex max-w-full items-end gap-2 rounded-lg outline-none transition-colors hover:text-interaction focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label={`Open ${title.toLowerCase()} in Google Maps`}
            >
              <span className="min-w-0">{addressContent}</span>
              <ExternalLink
                className="mb-1 size-4 shrink-0 stroke-interaction"
                aria-hidden="true"
              />
            </a>
          ) : (
            addressContent
          )}
          {children}
        </div>
        {action ? <div className="absolute right-3 top-3">{action}</div> : null}
      </div>
    </DetailWell>
  )
}

function ContactDetailRow({ icon: Icon, label, children, action = null }) {
  return (
    <DetailWell compact tone="mint" className="flex min-w-0 items-center gap-3">
      <Icon className="size-4 shrink-0 stroke-chart-3" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <Typography variant="eyebrow">{label}</Typography>
        <div className="mt-1.5 min-w-0">{children}</div>
      </div>
      {action}
    </DetailWell>
  )
}

export function CustomerContactAside({
  customer,
  onEditCustomer,
  onEditPhysicalAddress,
  onEditBillingAddress,
  onEditContacts,
}) {
  const { formatPhone, formatToE164 } = usePhoneFormatter()
  const [, copyToClipboard] = useCopyToClipboard()
  const phoneHref = customer.phone ? `tel:${formatToE164(customer.phone)}` : null
  const emailHref = customer.email ? `mailto:${customer.email}` : null

  const copyContactValue = async (value, successMessage) => {
    if (!value) {
      return
    }

    try {
      await copyToClipboard(value)
      toast.success(successMessage)
    } catch {
      toast.error('Failed to copy contact detail')
    }
  }

  return (
    <>
      <DetailCard accent="purchaser" contentClassName="space-y-4 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <DetailIconBadge icon={Phone} tone="mint" />
            <div className="min-w-0">
              <Typography variant="cardTitle">Contact Information</Typography>
            </div>
          </div>
          <Can do="edit" on="customers">
            <IconActionButton
              icon={Pencil}
              label="Edit contact information"
              className={cn(detailSurfaceActionClass, 'shrink-0')}
              onClick={onEditCustomer}
            />
          </Can>
        </div>
        <div className="space-y-2.5">
          <ContactDetailRow
            icon={Phone}
            label="Phone"
            action={
              customer.phone ? (
                <IconActionButton
                  icon={Copy}
                  label="Copy customer phone"
                  variant="ghost"
                  className="shrink-0 rounded-xl"
                  onClick={() => copyContactValue(customer.phone, 'Phone number copied')}
                />
              ) : null
            }
          >
            {phoneHref ? (
              <a
                href={phoneHref}
                className={cn(
                  contactDetailValueClass,
                  'w-fit text-interaction underline-offset-4 hover:underline'
                )}
              >
                {formatPhone(customer.phone)}
              </a>
            ) : (
              <Typography variant="body" className="block">
                (None provided)
              </Typography>
            )}
          </ContactDetailRow>
          <ContactDetailRow
            icon={Mail}
            label="Email"
            action={
              customer.email ? (
                <IconActionButton
                  icon={Copy}
                  label="Copy customer email"
                  variant="ghost"
                  className="shrink-0 rounded-xl"
                  onClick={() => copyContactValue(customer.email, 'Email copied')}
                />
              ) : null
            }
          >
            {emailHref ? (
              <a
                href={emailHref}
                className={cn(
                  contactDetailValueClass,
                  'w-fit text-interaction underline-offset-4 hover:underline'
                )}
              >
                {customer.email}
              </a>
            ) : (
              <Typography variant="body" className="block">
                (None provided)
              </Typography>
            )}
          </ContactDetailRow>
          <ContactDetailRow icon={customer.is_business ? Building2 : User} label="Customer Type">
            <CustomerTypeBadge isBusiness={Boolean(customer.is_business)} />
          </ContactDetailRow>
        </div>
        <AddressBlock
          title="Physical Address"
          address={customer.physical_address}
          action={
            <Can do="edit" on="customers">
              <IconActionButton
                icon={Pencil}
                className={detailSurfaceActionClass}
                label={customer.physical_address ? 'Edit physical address' : 'Add physical address'}
                onClick={onEditPhysicalAddress}
              />
            </Can>
          }
        />
        <AddressBlock
          title="Billing Address"
          address={customer.billing_address}
          hideAddress={Boolean(customer.physical_address_is_billing)}
          action={
            <Can do="edit" on="customers">
              <IconActionButton
                icon={Pencil}
                label="Edit billing address"
                className={detailSurfaceActionClass}
                onClick={onEditBillingAddress}
              />
            </Can>
          }
        >
          {customer.physical_address_is_billing ? (
            <Typography variant="supporting" className="mt-1 block">
              Same as physical address.
            </Typography>
          ) : null}
        </AddressBlock>
      </DetailCard>

      {customer.is_business || (customer.contacts ?? []).length > 0 ? (
        <DetailCard accent="purchaser" contentClassName="space-y-4 p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <DetailIconBadge icon={ContactRound} tone="mint" />
              <div className="min-w-0">
                <Typography variant="cardTitle">Contacts</Typography>
              </div>
            </div>
            <Can do="edit" on="customers">
              <IconActionButton
                icon={Pencil}
                label="Edit contacts"
                className={cn(detailSurfaceActionClass, 'shrink-0')}
                disabled={!customer.is_business}
                onClick={onEditContacts}
              />
            </Can>
          </div>
          <Typography variant="supporting" className="block">
            People at this business.
          </Typography>
          {!customer.is_business ? (
            <Typography variant="supporting" className="block">
              Switch this customer to Business before changing contacts.
            </Typography>
          ) : null}
          {(customer.contacts ?? []).length > 0 ? (
            <div className="space-y-2.5">
              {(customer.contacts ?? []).slice(0, 4).map((contact) => (
                <DetailWell
                  key={contact.id}
                  compact
                  tone="mint"
                  className="flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <Typography variant="labelStrong">{contact.name}</Typography>
                      {contact.is_primary ? (
                        <Badge variant="outline" className="shrink-0">
                          Primary
                        </Badge>
                      ) : null}
                    </div>
                    <Typography variant="supporting" className="mt-1 block">
                      {[contact.email, contact.phone ? formatPhone(contact.phone) : null]
                        .filter(Boolean)
                        .join(' · ') || 'No contact details'}
                    </Typography>
                  </div>
                </DetailWell>
              ))}
            </div>
          ) : (
            <Typography variant="supporting" className="block">
              No contacts added yet.
            </Typography>
          )}
        </DetailCard>
      ) : null}
    </>
  )
}
